import React, { useState } from "react";
import { Button, Checkbox } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { updateDoc, doc as firestoreDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { showCustomToast } from "../Misc/CustomToast";
import SignaturePadComponentV2 from "../Signature/SignaturePadComponentV2";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadString,
} from "firebase/storage";
import CircularProgress from "@mui/material/CircularProgress";
import "./BagSearchChecklistRedesign.css";
import { StandardDrawerFooter, StandardDrawerHeader } from "./StandardDrawer";

function AddClientIntakeAndBagSearchChecklist(props) {
  const { clientId, closemodal } = props;
  const [signatureDataURL, setSignatureDataURL] = useState(null);
  const [isSignatureValidated, setIsSignatureValidated] = useState(false);
  const [, setIsSignatureStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSignatureSave = (dataURL) => {
    setSignatureDataURL(dataURL);
  };

  const uploadSignature = async () => {
    const storage = getStorage();
    const fileName = `${clientId}_bagsearch_signature.png`;
    const signatureRef = ref(
      storage,
      `MandatoryForms/ClientIntakeAndBagSearchChecklist/${fileName}`
    );
    await uploadString(signatureRef, signatureDataURL, "data_url");
    const downloadURL = await getDownloadURL(signatureRef);
    return downloadURL;
  };

  // Définition des sections et items de la checklist
  const checklistSections = [
    {
      title: "Initial Greeting and Explanation",
      items: [
        "Explain the importance of the search process for safety and compliance.",
        "Inform the client of the steps involved and ask if they have any questions.",
      ],
    },
    {
      title: "Body and Personal Item Check",
      items: [
        "Emptying Pockets: Ask the client to empty all pockets and place items on a tray.",
        "Conduct a pat-down from head to toe, checking: Quick Pat-Down, Waistband, Underarms, Inside collars and cuffs, Socks and shoes.",
      ],
    },
    {
      title: "Bag and Luggage Search",
      items: [
        "External Inspection: Check for signs of tampering (e.g., cut fabric, sewn-in pockets, broken zippers). Look for unusual bulges or weight distribution.",
        "Internal Inspection: Open all zippers, pockets, and compartments. Shake out all clothing and inspect seams and linings for hidden pockets. Check the bottoms of bags and behind any interior linings. Inspect containers, toiletry bags, and electronics cases.",
      ],
    },
    {
      title: "Main Luggage Search",
      items: [
        "External Inspection: Look for any signs of tampering or unusual modifications. Check handles, wheels, and any external pockets.",
        "Internal Inspection: Open all compartments and remove items. Shake out clothing and check for hidden pockets. Inspect seams, linings, and any rigid structures for hidden items. Thoroughly check shoes, books, and any other personal items.",
      ],
    },
    {
      title: "Electronic Devices Check",
      items: [
        "Ask the client to declare all electronic devices (e.g., laptops, tablets, phones). Document each device and the client's stated need for them.",
      ],
    },
    {
      title: "Documentation",
      items: [
        "Quick Note: Immediately after the search, document the client’s name and date of intake, details of the search process (what was checked and any items found), and information explained to the client about the search and next steps. Note that the client has been shown around or that orientation will occur later.",
        "End of Shift Note: Briefly mention that the bag search was completed. Refer to the quick note for detailed information.",
      ],
    },
  ];

  // Initialisation de l'état des réponses (clé = "sectionIndex_itemIndex")
  const initialResponses = {};
  checklistSections.forEach((section, sIndex) => {
    section.items.forEach((_, iIndex) => {
      initialResponses[`${sIndex}_${iIndex}`] = false;
    });
  });
  const [responses, setResponses] = useState(initialResponses);

  const handleToggle = (key) => {
    setResponses((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFormSubmit = async () => {
    // Vérifier que toutes les cases sont cochées
    const allChecked = Object.values(responses).every((val) => val === true);
    if (!allChecked) {
      showCustomToast(
        "Please complete the checklist by checking all items.",
        "error"
      );
      return;
    }
    // Vérifier que la signature est fournie et validée
    if (!signatureDataURL || !isSignatureValidated) {
      showCustomToast("Please provide a valid signature.", "error");
      return;
    }

    setIsLoading(true);
    try {
      const signatureURL = await uploadSignature();
      // Enregistrer les réponses dans la collection "ClientIntakeAndBagSearchChecklist"
      const checklistDocRef = doc(
        db,
        "ClientIntakeAndBagSearchChecklist",
        clientId
      );
      await setDoc(checklistDocRef, {
        responses,
        signatureURL,
        signedOn: serverTimestamp(),
      });
      // Mettre à jour le document client dans "clients"
      const clientDocRef = firestoreDoc(db, "clients", clientId);
      await updateDoc(clientDocRef, {
        clientIntakeAndBagSearchOk: true,
        clientIntakeAndBagSearchDateSigned: serverTimestamp(),
      });
      showCustomToast("Checklist successfully submitted!", "success");
      setIsLoading(false);
      closemodal();
    } catch (error) {
      console.error("Error submitting checklist:", error);
      showCustomToast("Error submitting checklist", "error");
      setIsLoading(false);
    }
  };

  return (
    <div className="bag-checklist-shell">
      <StandardDrawerHeader
        eyebrow="Admission form"
        title="Client intake and bag search checklist"
        subtitle={`${props.firstName} ${props.lastName}`.trim()}
        onClose={closemodal}
      />

      <div className="bag-checklist-body">
        <div className="bag-checklist-intro">
          <strong>Required checklist</strong>
          <span>All items must be checked before submitting the form.</span>
        </div>

        <div className="bag-checklist-sections">
          {checklistSections.map((section, sIndex) => (
            <section className="bag-checklist-card" key={sIndex}>
              <h3>{section.title}</h3>
              <div className="bag-checklist-items">
                {section.items.map((item, iIndex) => {
                  const key = `${sIndex}_${iIndex}`;
                  return (
                    <label className="bag-checklist-item" key={key}>
                      <Checkbox
                        checked={responses[key]}
                        onChange={() => handleToggle(key)}
                        className="bag-checklist-checkbox"
                      />
                      <span>{item}</span>
                    </label>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <section className="bag-checklist-card bag-checklist-signature-card">
          <h3>Client signature</h3>
          <SignaturePadComponentV2
            onSignatureSave={handleSignatureSave}
            signatureDataURL={signatureDataURL}
            onSignatureStart={() => setIsSignatureStarted(true)}
            onSignatureValidate={setIsSignatureValidated}
          />
        </section>
      </div>

      <StandardDrawerFooter className="bag-checklist-footer">
        <Button
          className="bag-checklist-secondary-button"
          variant="contained"
          onClick={closemodal}
        >
          Cancel
        </Button>
        <Button
          className="bag-checklist-primary-button"
          variant="contained"
          onClick={handleFormSubmit}
          disabled={isLoading}
          startIcon={isLoading ? null : <SaveIcon />}
        >
          {isLoading ? <CircularProgress size={22} color="inherit" /> : "Submit"}
        </Button>
      </StandardDrawerFooter>
    </div>
  );
}

export default AddClientIntakeAndBagSearchChecklist;
